import { BaseApi } from './base';

class LessonProgramsApi extends BaseApi {
  constructor() {
    super('admin/lesson-programs');
  }

  /**
   * Get all lesson programs with optional filtering
   * @param {Object} params - Filter parameters (lesson_type, subject_id, published, created_by)
   */
  getLessonPrograms(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(queryString ? `?${queryString}` : '');
  }

  /**
   * Get a specific lesson program by ID
   * @param {number} id
   */
  getLessonProgram(id) {
    return this.get(`/${id}`);
  }

  /**
   * Create a new lesson program
   * @param {Object} data - Lesson program data
   */
  createLessonProgram(data) {
    return this.post('', data);
  }

  /**
   * Update an existing lesson program
   * @param {number} id
   * @param {Object} data - Updated lesson program data (FormData with _method=PUT)
   */
  updateLessonProgram(id, data) {
    // Use POST with _method=PUT for FormData compatibility
    return this.post(`/${id}`, data);
  }

  /**
   * Delete a lesson program
   * @param {number} id
   */
  deleteLessonProgram(id) {
    return this.delete(`/${id}`);
  }

  /**
   * Add a component to a lesson program
   * @param {number} id - Lesson program ID
   * @param {Object} componentData - Component data
   */
  addComponent(id, componentData) {
    return this.post(`/${id}/components`, componentData);
  }

  /**
   * Get allowed component types for a lesson program
   * @param {number} id - Lesson program ID
   */
  getAllowedComponentTypes(id) {
    return this.get(`/${id}/allowed-component-types`);
  }

  /**
   * Auto-populate control lesson with tests and essays
   * @param {number} id - Lesson program ID
   */
  autoPopulate(id) {
    return this.post(`/${id}/auto-populate`);
  }

  /**
   * Get available test packs for a subject
   * @param {number} subjectId
   */
  getAvailableTestPacks(subjectId) {
    return this.get(`/subjects/${subjectId}/test-packs`);
  }

  /**
   * Get available essay questions for a subject
   * @param {number} subjectId
   */
  getAvailableEssayQuestions(subjectId) {
    return this.get(`/subjects/${subjectId}/essay-questions`);
  }

  /**
   * Get lesson programs by type
   * @param {string} type - 'regular' or 'control'
   */
  getLessonProgramsByType(type) {
    return this.getLessonPrograms({ lesson_type: type });
  }

  /**
   * Get control lessons only
   */
  getControlLessons() {
    return this.getLessonProgramsByType('control');
  }

  /**
   * Get regular lessons only
   */
  getRegularLessons() {
    return this.getLessonProgramsByType('regular');
  }

  /**
   * Get published lesson programs
   */
  getPublishedLessonPrograms() {
    return this.getLessonPrograms({ published: true });
  }

  /**
   * Get lesson programs for a specific subject
   * @param {number} subjectId
   */
  getLessonProgramsBySubject(subjectId) {
    return this.getLessonPrograms({ subject_id: subjectId });
  }
}

export default new LessonProgramsApi();